import {
    Box,
    Button,
    Heading,
    Text,
    VStack,
    HStack,
    Badge,
    IconButton,
    useToast,
    Spinner,
    Flex,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useColorModeValue,
    Icon,
    Select,
    Input,
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdCheckCircle, MdError, MdUploadFile, MdDelete, MdAdd, MdDownload } from 'react-icons/md';
import {
    useFinalSubmittals,
    useCreateFinalSubmittal,
    useUpdateFinalSubmittal,
    useDeleteFinalSubmittal,
} from '../../services/submittals.hooks';

interface FinalSubmittalsTabProps {
    pursuitId: number;
}

export function FinalSubmittalsTab({ pursuitId }: FinalSubmittalsTabProps) {
    const toast = useToast();
    const panelBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    // Data Hooks
    const { data: submittals = [], isLoading } = useFinalSubmittals(pursuitId);
    const createSubmittalMutation = useCreateFinalSubmittal();

    const [isCreating, setIsCreating] = useState(false);

    const handleAddSubmittal = () => {
        setIsCreating(true);
        createSubmittalMutation.mutate({
            pursuitId,
            data: { name: "New Final Submittal", status: "required", category: "preconstruction" }
        }, {
            onSuccess: () => {
                toast({ title: "Submittal added", status: "success", duration: 2000 });
                setIsCreating(false);
            },
            onError: () => setIsCreating(false)
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={6}>

            {/* Header & Actions */}
            <Flex
                justify="space-between"
                align="center"
                p={6}
                bg={panelBg}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                shadow="sm"
                wrap="wrap"
                gap={4}
            >
                <Box>
                    <Heading size="md" mb={1}>Post-Award Submittals Vault</Heading>
                    <Text fontSize="sm" color="gray.500">Track final requirements like insurance, bonds, and preconstruction plans.</Text>
                </Box>
                <HStack>
                    <Button
                        leftIcon={<MdAdd />}
                        colorScheme="blue"
                        onClick={handleAddSubmittal}
                        isLoading={isCreating}
                    >
                        Add Record
                    </Button>
                </HStack>
            </Flex>

            {/* Document Vault */}
            <VStack spacing={3} align="stretch">
                {isLoading ? (
                    <Flex justify="center" p={10}><Spinner /></Flex>
                ) : submittals.length === 0 ? (
                    <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        p={10}
                        bg={panelBg}
                        borderRadius="xl"
                        borderStyle="dashed"
                        borderWidth="2px"
                        borderColor="gray.300"
                    >
                        <Icon as={MdUploadFile} boxSize={10} color="gray.300" mb={3} />
                        <Text color="gray.500" fontWeight="medium">No final submittals tracked.</Text>
                        <Text fontSize="sm" color="gray.400" mb={4}>Click "Add Record" to start tracking.</Text>
                    </Flex>
                ) : (
                    submittals.map((sub: any) => (
                        <FinalSubmittalRow
                            key={sub.id}
                            submittal={sub}
                        />
                    ))
                )}
            </VStack>
        </Box>
    );
}

// Sub-component for individual row
function FinalSubmittalRow({ submittal }: { submittal: any }) {
    const bg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    const updateSubmittalMutation = useUpdateFinalSubmittal();
    const deleteSubmittalMutation = useDeleteFinalSubmittal();

    const handleStatusChange = (newStatus: string) => {
        updateSubmittalMutation.mutate({ id: submittal.id, data: { status: newStatus } });
    };

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        updateSubmittalMutation.mutate({ id: submittal.id, data: { category: e.target.value } });
    };

    const handleExpirationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        updateSubmittalMutation.mutate({
            id: submittal.id,
            data: { expiration_date: val ? new Date(val).toISOString() : null }
        });
    };

    const handleDelete = () => {
        if (confirm('Are you sure you want to delete this record?')) {
            deleteSubmittalMutation.mutate(submittal.id);
        }
    };

    const statusColors: Record<string, string> = {
        required: 'orange',
        uploaded: 'blue',
        submitted: 'purple',
        approved: 'green',
        needs_revision: 'red'
    };

    // calculate expiration status
    let isExpiring = false;
    let isExpired = false;
    if (submittal.expiration_date) {
        const expDate = new Date(submittal.expiration_date);
        const now = new Date();
        const diffMs = expDate.getTime() - now.getTime();
        const days = diffMs / (1000 * 60 * 60 * 24);
        if (days < 0) isExpired = true;
        else if (days <= 30) isExpiring = true;
    }

    return (
        <Box
            p={4}
            bg={bg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={isExpired ? 'red.400' : isExpiring ? 'orange.400' : borderColor}
            borderLeftWidth="4px"
            borderLeftColor={`${statusColors[submittal.status] || 'gray'}.400`}
            shadow="sm"
            transition="all 0.2s"
            _hover={{ shadow: 'md' }}
        >
            <Flex gap={4} align="center" wrap="wrap">

                {/* Status indicator */}
                <Menu>
                    <MenuButton
                        as={IconButton}
                        icon={
                            submittal.status === 'approved' ? <MdCheckCircle /> :
                                submittal.status === 'needs_revision' ? <MdError /> : <Box w={3} h={3} borderRadius="full" bg={`${statusColors[submittal.status]}.400`} />
                        }
                        variant="ghost"
                        colorScheme={statusColors[submittal.status] || 'gray'}
                        size="sm"
                    />
                    <MenuList>
                        <MenuItem onClick={() => handleStatusChange('required')}>Required</MenuItem>
                        <MenuItem onClick={() => handleStatusChange('uploaded')}>Uploaded</MenuItem>
                        <MenuItem onClick={() => handleStatusChange('submitted')}>Submitted</MenuItem>
                        <MenuItem onClick={() => handleStatusChange('approved')}>Approved</MenuItem>
                        <MenuItem onClick={() => handleStatusChange('needs_revision')}>Needs Revision</MenuItem>
                    </MenuList>
                </Menu>

                <Box flex={1} minW="200px">
                    <HStack mb={1}>
                        <Badge colorScheme={statusColors[submittal.status] || 'gray'} fontSize="xx-small">
                            {submittal.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                        {isExpired && <Badge colorScheme="red" fontSize="xx-small">EXPIRED</Badge>}
                        {isExpiring && !isExpired && <Badge colorScheme="orange" fontSize="xx-small">EXPIRING SOON</Badge>}
                    </HStack>
                    <Text fontWeight="medium" fontSize="sm">
                        {submittal.name}
                    </Text>
                </Box>

                {/* Category Selection */}
                <Box w={{ base: "100%", md: "180px" }}>
                    <Select
                        size="sm"
                        value={submittal.category || 'other'}
                        onChange={handleCategoryChange}
                    >
                        <option value="preconstruction">Preconstruction</option>
                        <option value="compliance">Compliance & Diversity</option>
                        <option value="financial">Financial & Legal</option>
                        <option value="other">Other</option>
                    </Select>
                </Box>

                {/* Expiration Tracking */}
                <Box w={{ base: "100%", md: "150px" }}>
                    <Input
                        type="date"
                        size="sm"
                        placeholder="Expiration"
                        value={submittal.expiration_date ? new Date(submittal.expiration_date).toISOString().split('T')[0] : ''}
                        onChange={handleExpirationChange}
                    />
                </Box>

                {/* Actions */}
                <HStack spacing={2}>
                    <Button
                        size="sm"
                        leftIcon={<MdUploadFile />}
                        colorScheme="blue"
                        variant="outline"
                    >
                        Upload
                    </Button>
                    <IconButton
                        aria-label="Download"
                        icon={<MdDownload />}
                        size="sm"
                        variant="ghost"
                        isDisabled={!submittal.file_url && !submittal.file_path}
                    />
                    <IconButton
                        aria-label="Delete"
                        icon={<MdDelete />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={handleDelete}
                    />
                </HStack>
            </Flex>
        </Box>
    );
}
